import { useState, useEffect } from 'react'
import { lumi } from '@/lib/lumi'
import toast from 'react-hot-toast'
import {LogOut, Eye, Trash2, Download, Copy, MessageCircleDashed as MessageCircle} from 'lucide-react'
import { OLEOS_INFO } from '@/types/diagnostico'
import AdminModalTabs from '@/components/AdminModalTabs'
import ScriptVendas from '@/components/ScriptVendas'
import { copiarTexto } from '@/utils/clipboard'

const SENHA_ADMIN = 'eusousuprida520'

// Função para decodificar strings Unicode escapadas
const decodeUnicodeString = (str: any): any => {
  if (typeof str === 'string') {
    try {
      // Se a string contém sequências Unicode escapadas (\uXXXX)
      if (str.includes('\\u')) {
        return JSON.parse(`"${str}"`)
      }
      return str
    } catch {
      return str
    }
  }
  if (Array.isArray(str)) {
    return str.map(item => decodeUnicodeString(item))
  }
  if (str && typeof str === 'object') {
    const decoded: any = {}
    for (const key in str) {
      decoded[key] = decodeUnicodeString(str[key])
    }
    return decoded
  }
  return str
}

function Admin() {
  const [autenticado, setAutenticado] = useState(false)
  const [senha, setSenha] = useState('')
  const [leads, setLeads] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [filtroStatus, setFiltroStatus] = useState<string>('todos')
  const [busca, setBusca] = useState('')
  const [leadSelecionado, setLeadSelecionado] = useState<any>(null)
  const [modalAberto, setModalAberto] = useState(false)

  // Verificar autenticação ao montar componente
  useEffect(() => {
    const isAutenticado = localStorage.getItem('admin_autenticado') === 'true'
    if (isAutenticado) {
      setAutenticado(true)
    }
  }, [])

  // Autenticação
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (senha === SENHA_ADMIN) {
      setAutenticado(true)
      localStorage.setItem('admin_autenticado', 'true')
      toast.success('Login realizado!')
      carregarLeads()
    } else {
      toast.error('Senha incorreta')
    }
  }

  const handleLogout = () => {
    setAutenticado(false)
    setSenha('')
    setLeads([])
    localStorage.removeItem('admin_autenticado')
    toast.success('Logout realizado!')
  }

  // Carregar leads automaticamente se já estiver autenticado
  useEffect(() => {
    if (autenticado) {
      carregarLeads()
    }
  }, [autenticado])

  // Carregar leads
  const carregarLeads = async () => {
    setLoading(true)
    try {
      const resultado = await lumi.entities.leads.list({
        sort: { data_hora: -1 },
      })
      // Decodificar caracteres Unicode escapados em todos os leads
      const leadsDecodificados = (resultado.list || []).map(lead => decodeUnicodeString(lead))
      setLeads(leadsDecodificados)
    } catch (error) {
      console.error('Erro ao carregar leads:', error)
      toast.error('Erro ao carregar leads')
    } finally {
      setLoading(false)
    }
  }

  // Atualizar status
  const atualizarStatus = async (leadId: string, novoStatus: string) => {
    try {
      await lumi.entities.leads.update(leadId, {
        status: novoStatus,
        updatedAt: new Date().toISOString(),
      })
      toast.success('Status atualizado!')
      carregarLeads()
    } catch (error) {
      console.error('Erro ao atualizar status:', error)
      toast.error('Erro ao atualizar status')
    }
  }

  // Excluir lead
  const excluirLead = async (leadId: string) => {
    if (!confirm('Tem certeza que deseja excluir este lead?')) return
    try {
      await lumi.entities.leads.delete(leadId)
      toast.success('Lead excluído!')
      carregarLeads()
      setModalAberto(false)
    } catch (error) {
      console.error('Erro ao excluir lead:', error)
      toast.error('Erro ao excluir lead')
    }
  }

  // Exportar CSV
  const exportarCSV = () => {
    const headers = ['Data/Hora', 'Nome', 'WhatsApp', 'E-mail', 'Status', 'Óleos Indicados', 'Clicou no WhatsApp', 'Compartilhou']
    const rows = leadsFiltrados.map(lead => [
      new Date(lead.data_hora).toLocaleString('pt-BR'),
      lead.nome,
      lead.whatsapp,
      lead.email,
      lead.status,
      (lead.oleos_indicados || []).join('; '),
      lead.clicou_whatsapp || 'Não',
      lead.compartilhou || 'Não',
    ])

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers, ...rows].map(e => e.join(',')).join('\n')

    const link = document.createElement('a')
    link.setAttribute('href', encodeURI(csvContent))
    link.setAttribute('download', `leads_${new Date().toISOString().split('T')[0]}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Filtrar leads
  const leadsFiltrados = leads.filter(lead => {
    const matchStatus = filtroStatus === 'todos' || lead.status === filtroStatus
    const matchBusca =
      !busca ||
      lead.nome?.toLowerCase().includes(busca.toLowerCase()) ||
      lead.whatsapp?.includes(busca)
    return matchStatus && matchBusca
  })

  // Estatísticas
  const stats = {
    total: leads.length,
    novo: leads.filter(l => l.status === 'Novo lead').length,
    contato: leads.filter(l => l.status === 'Em contato').length,
    agendado: leads.filter(l => l.status === 'Agendado').length,
    convertido: leads.filter(l => l.status === 'Convertido').length,
    naoRespondeu: leads.filter(l => l.status === 'Não respondeu').length,
    naoQualificado: leads.filter(l => l.status === 'Não qualificado').length,
  }

  // Função para obter cores do status
  const obterCoresStatus = (status: string) => {
    const cores: Record<string, { bg: string; text: string }> = {
      'Novo lead': { bg: '#E6F1FB', text: '#185FA5' },
      'Em contato': { bg: '#FAEEDA', text: '#854F0B' },
      'Agendado': { bg: '#F3E8FF', text: '#6B21A8' },
      'Convertido': { bg: '#D8F3DC', text: '#2D6A4F' },
      'Não respondeu': { bg: '#FCEBEB', text: '#A32D2D' },
      'Não qualificado': { bg: '#F3F4F6', text: '#4B5563' },
    }
    return cores[status] || { bg: '#F3F4F6', text: '#6B7280' }
  }

  // Labels curtos para sintomas
  const labelsPerguntas: Record<string, string> = {
    'sono': 'Sono',
    'disposicao': 'Disposição',
    'foco': 'Foco',
    'memoria': 'Memória',
    'ansiedade': 'Ansiedade física',
    'peso': 'Peso',
    'dores': 'Dores recorrentes',
    'medicamentos': 'Medicamentos',
    'inflamacoes': 'Inflamações',
    'intestino': 'Intestino',
    'vergonha': 'Vergonha',
    'culpa': 'Culpa',
    'apatia': 'Apatia',
    'tristeza': 'Tristeza',
    'medo': 'Medo',
    'escassez': 'Escassez',
    'raiva': 'Raiva',
    'orgulho': 'Orgulho',
    'estagnacao': 'Estagnação',
    'desequilibrio': 'Desequilíbrio geral',
  }

  // Montar texto formatado do resumo do lead
  const montarResumo = (lead: any): string => {
    let texto = `*Nome:* ${lead.nome || ''}\n*WhatsApp:* ${lead.whatsapp || ''}\n\n*RESPOSTAS DO DIAGNÓSTICO*\n`

    texto += `\n*Condições de saúde:*\n`
    if (lead.condicoes_saude?.length > 0) {
      lead.condicoes_saude.forEach((c: string) => { texto += `- ${c}\n` })
    } else {
      texto += `- Nenhuma\n`
    }

    texto += `\n*Sintomas respondidos como SIM:*\n`
    if (lead.respostas && Object.keys(lead.respostas).length > 0) {
      const sintomas = Object.entries(lead.respostas)
        .filter(([_, valor]) => valor)
        .map(([key]) => labelsPerguntas[key] || key)
      if (sintomas.length > 0) {
        sintomas.forEach(s => { texto += `- ${s}\n` })
      } else {
        texto += `- Nenhum\n`
      }
    } else {
      texto += `- Nenhum\n`
    }

    texto += `\n*Óleos essenciais indicados:*\n`
    if (lead.oleos_indicados?.length > 0) {
      lead.oleos_indicados.forEach((o: string) => { texto += `- ${o}\n` })
    } else {
      texto += `- Nenhum\n`
    }

    texto += `\n*Experiência com óleos essenciais:* ${lead.experiencia_oleos || 'Não informado'}\n`
    texto += `\n*Prontidão para começar:* ${lead.prontidao || 'Não informado'}\n`
    texto += `\n*Maior obstáculo:* ${lead.obstaculo || 'Não informado'}\n`

    texto += `\n*Interesses:*\n`
    if (lead.interesses?.length > 0) {
      lead.interesses.forEach((i: string) => { texto += `- ${i}\n` })
    } else {
      texto += `- Não informado\n`
    }

    const prioridades = lead.prioridades || []
    if (prioridades.length > 0) {
      texto += `\n*Prioridades selecionadas:*\n`
      prioridades.forEach((p: string) => { texto += `- ${p}\n` })
    } else {
      texto += `\n*O que mais quer resolver na saúde:*\n${lead.objetivo_saude || 'Não informado'}\n`
    }

    return texto
  }

  // Copiar resumo para clipboard
  const copiarResumo = async (lead: any) => {
    const texto = montarResumo(lead)
    try {
      await copiarTexto(texto)
      toast.success('Resumo copiado!')
    } catch {
      toast.error('Erro ao copiar')
    }
  }

  // Formatar número para WhatsApp (extrair apenas dígitos, mantém o prefixo do país)
  const formatarNumeroWhatsApp = (whatsapp: string): string => {
    const apenasDigitos = (whatsapp || '').replace(/\D/g, '')
    return apenasDigitos
  }

  // Exibir WhatsApp formatado com código do país
  const exibirWhatsAppFormatado = (whatsapp: string, pais: string): string => {
    const digitos = (whatsapp || '').replace(/\D/g, '')
    if (pais === 'Portugal') {
      // +351 9XX XXX XXX
      if (digitos.startsWith('351')) {
        const numero = digitos.slice(3)
        return `+351 ${numero}`
      }
      return `+351 ${digitos}`
    } else {
      // +55 (DDD) 9 9999-9999
      if (digitos.startsWith('55')) {
        const numero = digitos.slice(2)
        if (numero.length === 11) {
          return `+55 (${numero.slice(0, 2)}) ${numero.slice(2, 3)} ${numero.slice(3, 7)}-${numero.slice(7)}`
        }
        return `+55 ${numero}`
      }
      if (digitos.length === 11) {
        return `+55 (${digitos.slice(0, 2)}) ${digitos.slice(2, 3)} ${digitos.slice(3, 7)}-${digitos.slice(7)}`
      }
      return `+55 ${digitos}`
    }
  }

  // Abrir resumo no WhatsApp da cliente
  const abrirWhatsAppResumo = (lead: any) => {
    const mensagemFormatada = montarResumo(lead)
    const numero = formatarNumeroWhatsApp(lead.whatsapp)
    if (!numero) {
      toast.error('WhatsApp da cliente não disponível')
      return
    }
    const url = `https://wa.me/${numero}?text=${encodeURIComponent(mensagemFormatada)}`
    window.open(url, '_blank')
  }

  if (!autenticado) {
    return (
      <div className="min-h-screen bg-bege-fundo flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-lg">
          <h1 className="text-3xl font-cormorant font-semibold text-texto-escuro mb-6 text-center">
            Painel Administrativo
          </h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-texto-escuro mb-2">Senha</label>
              <input
                type="password"
                value={senha}
                onChange={e => setSenha(e.target.value)}
                className="w-full px-4 py-3 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                placeholder="Digite a senha"
              />
            </div>
            <button
              type="submit"
              className="w-full bg-verde-escuro text-white py-3 rounded-full font-medium hover:bg-opacity-90 transition-all"
            >
              Entrar
            </button>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-bege-fundo">
      {/* Header */}
      <div className="bg-verde-escuro text-white px-6 py-6">
        <div className="container mx-auto flex justify-between items-center">
          <h1 className="text-2xl font-cormorant font-semibold">
            Painel Administrativo — Diagnóstico de Saúde e Emoções
          </h1>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 bg-white text-verde-escuro px-4 py-2 rounded-full hover:bg-opacity-90 transition-all"
          >
            <LogOut className="w-4 h-4" />
            Sair
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Estatísticas */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-verde-escuro mb-2">{stats.total}</div>
            <div className="text-sm text-texto-suave">Total de leads</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-blue-600 mb-2">{stats.novo}</div>
            <div className="text-sm text-texto-suave">Novo lead</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-yellow-600 mb-2">{stats.contato}</div>
            <div className="text-sm text-texto-suave">Em contato</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-purple-600 mb-2">{stats.agendado}</div>
            <div className="text-sm text-texto-suave">Agendado</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-green-600 mb-2">{stats.convertido}</div>
            <div className="text-sm text-texto-suave">Convertido</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-red-600 mb-2">{stats.naoRespondeu}</div>
            <div className="text-sm text-texto-suave">Não respondeu</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-gray-600 mb-2">{stats.naoQualificado}</div>
            <div className="text-sm text-texto-suave">Não qualificado</div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white p-6 rounded-xl shadow-md mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-texto-suave mb-2">Status</label>
              <select
                value={filtroStatus}
                onChange={e => setFiltroStatus(e.target.value)}
                className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
              >
                <option value="todos">Todos</option>
                <option value="Novo lead">Novo lead</option>
                <option value="Em contato">Em contato</option>
                <option value="Agendado">Agendado</option>
                <option value="Convertido">Convertido</option>
                <option value="Não respondeu">Não respondeu</option>
                <option value="Não qualificado">Não qualificado</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-texto-suave mb-2">Buscar</label>
              <input
                type="text"
                value={busca}
                onChange={e => setBusca(e.target.value)}
                placeholder="Nome ou WhatsApp"
                className="w-full px-4 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={exportarCSV}
                className="w-full flex items-center justify-center gap-2 bg-verde-escuro text-white px-4 py-2 rounded-xl hover:bg-opacity-90 transition-all"
              >
                <Download className="w-4 h-4" />
                Exportar CSV
              </button>
            </div>
          </div>
        </div>

        {/* Tabela */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-texto-suave">Carregando...</div>
          ) : leadsFiltrados.length === 0 ? (
            <div className="p-8 text-center text-texto-suave">Nenhum lead encontrado</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-bege-fundo">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Data/Hora</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Nome</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">WhatsApp</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">E-mail</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">País</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Óleos</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Clicou WhatsApp</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Compartilhou</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-bege-medio">
                  {leadsFiltrados.map(lead => (
                    <tr key={lead._id} className="hover:bg-bege-fundo">
                      <td className="px-4 py-3 text-sm text-texto-suave">
                        {new Date(lead.data_hora).toLocaleString('pt-BR')}
                      </td>
                      <td className="px-4 py-3 text-sm text-texto-escuro font-medium">{lead.nome}</td>
                      <td className="px-4 py-3 text-sm">
                        <a
                          href={`https://wa.me/${formatarNumeroWhatsApp(lead.whatsapp)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-verde-escuro hover:underline"
                        >
                          {exibirWhatsAppFormatado(lead.whatsapp, lead.pais)}
                        </a>
                      </td>
                      <td className="px-4 py-3 text-sm text-texto-suave">{lead.email}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${lead.pais === 'Portugal' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
                          {lead.pais === 'Portugal' ? '🇵🇹 Portugal' : '🇧🇷 Brasil'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-texto-suave">
                        {(lead.oleos_indicados || []).length} óleos
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            lead.clicou_whatsapp === 'Sim'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {lead.clicou_whatsapp || 'Não'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            lead.compartilhou === 'Sim'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {lead.compartilhou || 'Não'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="relative inline-block">
                          <select
                            value={lead.status}
                            onChange={e => atualizarStatus(lead._id, e.target.value)}
                            className="appearance-none px-4 py-2 pr-8 rounded-full text-sm font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-verde-claro"
                            style={{
                              backgroundColor: obterCoresStatus(lead.status).bg,
                              color: obterCoresStatus(lead.status).text,
                            }}
                          >
                            <option value="Novo lead" style={{ backgroundColor: '#E6F1FB', color: '#185FA5' }}>Novo lead</option>
                            <option value="Em contato" style={{ backgroundColor: '#FAEEDA', color: '#854F0B' }}>Em contato</option>
                            <option value="Agendado" style={{ backgroundColor: '#F3E8FF', color: '#6B21A8' }}>Agendado</option>
                            <option value="Convertido" style={{ backgroundColor: '#D8F3DC', color: '#2D6A4F' }}>Convertido</option>
                            <option value="Não respondeu" style={{ backgroundColor: '#FCEBEB', color: '#A32D2D' }}>Não respondeu</option>
                            <option value="Não qualificado" style={{ backgroundColor: '#F3F4F6', color: '#4B5563' }}>Não qualificado</option>
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <div className="flex flex-row items-center gap-2">
                          <button
                            onClick={() => {
                              setLeadSelecionado(lead)
                              setModalAberto(true)
                            }}
                            className="p-2 text-verde-escuro hover:bg-verde-muito-claro rounded-lg transition-colors"
                            title="Ver detalhes"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => copiarResumo(lead)}
                            className="p-2 text-verde-escuro hover:bg-verde-muito-claro rounded-lg transition-colors"
                            title="Copiar resumo"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => abrirWhatsAppResumo(lead)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Abrir no WhatsApp"
                          >
                            <MessageCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => excluirLead(lead._id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Excluir"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal de detalhes */}
      {modalAberto && leadSelecionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-verde-escuro text-white px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-cormorant font-semibold">Detalhes do prospecto (lead)</h2>
              <button
                onClick={() => setModalAberto(false)}
                className="text-white hover:text-gray-200 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              {/* SEÇÃO 1: RESPOSTAS DO DIAGNÓSTICO */}
              <div className="mb-8">
                <div
                  className="flex items-start gap-3 p-4 rounded-xl mb-6"
                  style={{ backgroundColor: '#FEE2E2', border: '1px solid #F87171' }}
                >
                  <div
                    className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: '#EF4444' }}
                  >
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
                  </div>
                  <div className="text-sm leading-relaxed whitespace-pre-line" style={{ color: '#991B1B' }}>
                    {`Essas são as respostas após o preenchimento do diagnóstico. Confira com atenção para entender as demandas da pessoa com quem você vai conversar no WhatsApp. Leia tudo antes de entrar em contato. Observe especialmente o momento dela, o principal obstáculo e as prioridades selecionadas.`}
                  </div>
                </div>
                <h3 className="text-2xl font-cormorant font-semibold text-verde-escuro mb-6 pb-3 border-b-2 border-verde-escuro">
                  Respostas do Diagnóstico
                </h3>

                <div className="space-y-6">
                  {/* Nome, WhatsApp, E-mail e País */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <div className="text-sm text-texto-suave mb-1 font-medium">Nome</div>
                      <div className="text-texto-escuro text-lg">{leadSelecionado.nome}</div>
                    </div>
                    <div>
                      <div className="text-sm text-texto-suave mb-1 font-medium">WhatsApp</div>
                      <div className="text-texto-escuro text-lg">{exibirWhatsAppFormatado(leadSelecionado.whatsapp, leadSelecionado.pais)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-texto-suave mb-1 font-medium">Idade</div>
                      <div className="text-texto-escuro text-lg">{leadSelecionado.idade ? `${leadSelecionado.idade} anos` : 'Não informado'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-texto-suave mb-1 font-medium">E-mail</div>
                      <div className="text-texto-escuro text-lg">{leadSelecionado.email || 'Não informado'}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-texto-suave mb-1 font-medium">País</div>
                      <div className="text-texto-escuro text-lg">
                        {leadSelecionado.pais === 'Portugal' ? '🇵🇹 Portugal' : leadSelecionado.pais === 'Estados Unidos' ? '🇺🇸 Estados Unidos' : leadSelecionado.pais === 'Outro' ? '🌍 Outro' : '🇧🇷 Brasil'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-texto-suave mb-1 font-medium">Data de preenchimento</div>
                      <div className="text-texto-escuro text-lg">{leadSelecionado.data_hora ? new Date(leadSelecionado.data_hora).toLocaleString('pt-BR') : 'Não informado'}</div>
                    </div>
                  </div>

                  {/* Condições de saúde */}
                  <div>
                    <div className="text-sm text-texto-suave mb-2 font-medium">Condições de saúde:</div>
                    {leadSelecionado.condicoes_saude?.length > 0 ? (
                      <ul className="list-disc list-inside space-y-1">
                        {leadSelecionado.condicoes_saude.map((condicao: string, idx: number) => (
                          <li key={idx} className="text-texto-escuro">{condicao}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-texto-suave">Nenhuma</div>
                    )}
                  </div>

                  {/* Sintomas respondidos como SIM */}
                  <div>
                    <div className="text-sm text-texto-suave mb-2 font-medium">Sintomas respondidos como SIM:</div>
                    {leadSelecionado.respostas && Object.entries(leadSelecionado.respostas).filter(([_, valor]) => valor).length > 0 ? (
                      <ul className="list-disc list-inside space-y-1">
                        {Object.entries(leadSelecionado.respostas)
                          .filter(([_, valor]) => valor)
                          .map(([key]) => (
                            <li key={key} className="text-texto-escuro">{labelsPerguntas[key] || key}</li>
                          ))}
                      </ul>
                    ) : (
                      <div className="text-texto-suave">Nenhum</div>
                    )}
                  </div>

                  {/* Óleos essenciais indicados */}
                  <div>
                    <div className="text-sm text-texto-suave mb-2 font-medium">Óleos essenciais indicados:</div>
                    {leadSelecionado.oleos_indicados?.length > 0 ? (
                      <ul className="list-disc list-inside space-y-1">
                        {leadSelecionado.oleos_indicados.map((oleo: string, idx: number) => (
                          <li key={idx} className="text-texto-escuro">{oleo}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-texto-suave">Nenhum</div>
                    )}
                  </div>

                  {/* Experiência, Prontidão, Obstáculo */}
                  <div className="space-y-3">
                    <div>
                      <div className="text-sm text-texto-suave mb-1 font-medium">Experiência com óleos essenciais:</div>
                      <div className="text-texto-escuro">{leadSelecionado.experiencia_oleos || 'Não informado'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-texto-suave mb-1 font-medium">Prontidão para começar:</div>
                      <div className="text-texto-escuro">{leadSelecionado.prontidao || 'Não informado'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-texto-suave mb-1 font-medium">Maior obstáculo:</div>
                      <div className="text-texto-escuro">{leadSelecionado.obstaculo || 'Não informado'}</div>
                    </div>
                  </div>

                  {/* Interesses */}
                  <div>
                    <div className="text-sm text-texto-suave mb-2 font-medium">Interesse declarado:</div>
                    {leadSelecionado.interesses?.length > 0 ? (
                      <ul className="list-disc list-inside space-y-1">
                        {leadSelecionado.interesses.map((interesse: string, idx: number) => (
                          <li key={idx} className="text-texto-escuro">{interesse}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-texto-suave">Não informado</div>
                    )}
                  </div>

                  {/* Prioridades selecionadas / fallback texto livre */}
                  <div>
                    {leadSelecionado.prioridades?.length > 0 ? (
                      <>
                        <div className="text-sm text-texto-suave mb-2 font-medium">Prioridades selecionadas:</div>
                        <ul className="space-y-1">
                          {leadSelecionado.prioridades.map((p: string, i: number) => (
                            <li key={i} className="flex items-center gap-2 text-texto-escuro">
                              <span className="text-verde-escuro">✅</span> {p}
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <>
                        <div className="text-sm text-texto-suave mb-2 font-medium">O que mais quer resolver na saúde:</div>
                        <div className="text-texto-escuro whitespace-pre-wrap bg-bege-fundo p-4 rounded-lg">
                          {leadSelecionado.objetivo_saude || 'Não informado'}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Linha divisória */}
              <div className="border-t-2 border-bege-medio my-8"></div>

              {/* SEÇÃO 2: SCRIPT DE VENDAS */}
              <ScriptVendas lead={leadSelecionado} />

              {/* Linha divisória */}
              <div className="border-t-2 border-bege-medio my-8"></div>

              {/* DEMAIS SEÇÕES: Prescrição, Opções de Compra, Protocolo de Uso, Protocolo com Óleos que Tenho */}
              <AdminModalTabs lead={leadSelecionado} />

              {/* Linha divisória */}
              <div className="border-t-2 border-bege-medio my-8"></div>

              {/* Ações */}
              <div className="flex gap-3 pt-4 border-t border-bege-medio">
                <div className="flex-1 relative">
                  <select
                    value={leadSelecionado.status}
                    onChange={e => {
                      atualizarStatus(leadSelecionado._id, e.target.value)
                      setLeadSelecionado({ ...leadSelecionado, status: e.target.value })
                    }}
                    className="w-full appearance-none px-4 py-3 pr-10 rounded-xl text-base font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-verde-claro"
                    style={{
                      backgroundColor: obterCoresStatus(leadSelecionado.status).bg,
                      color: obterCoresStatus(leadSelecionado.status).text,
                    }}
                  >
                    <option value="Novo lead" style={{ backgroundColor: '#E6F1FB', color: '#185FA5' }}>Novo lead</option>
                    <option value="Em contato" style={{ backgroundColor: '#FAEEDA', color: '#854F0B' }}>Em contato</option>
                    <option value="Agendado" style={{ backgroundColor: '#F3E8FF', color: '#6B21A8' }}>Agendado</option>
                    <option value="Convertido" style={{ backgroundColor: '#D8F3DC', color: '#2D6A4F' }}>Convertido</option>
                    <option value="Não respondeu" style={{ backgroundColor: '#FCEBEB', color: '#A32D2D' }}>Não respondeu</option>
                    <option value="Não qualificado" style={{ backgroundColor: '#F3F4F6', color: '#4B5563' }}>Não qualificado</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <button
                  onClick={() => excluirLead(leadSelecionado._id)}
                  className="px-6 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Admin
